import { useEffect, useState, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  getTransactions,
  downloadTransactionReport,
  selectTransactions,
  selectTransactionsLoading,
} from "@/store/slices/authSlice";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import Loader from "@/components/loader/loader";

export default function TransactionHistory() {
  const dispatch = useAppDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const transactions = useAppSelector(selectTransactions);
  const isLoading = useAppSelector(selectTransactionsLoading);
  const itemsPerPage = 10;

  // Load transactions on mount
  useEffect(() => {
    dispatch(getTransactions());
  }, [dispatch]);

  // Filter transactions based on search term
  const filteredTransactions = useMemo(() => {
    if (!searchTerm) return transactions;
    const lower = searchTerm.toLowerCase();
    return transactions.filter(
      (txn) =>
        txn.reference?.toLowerCase().includes(lower) ||
        txn.status?.toLowerCase().includes(lower) ||
        txn.paymentMethod?.toLowerCase().includes(lower)
    );
  }, [transactions, searchTerm]);

  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentTransactions = filteredTransactions.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleDownload = () => {
    dispatch(downloadTransactionReport());
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "Completed":
        return "default";
      case "Pending":
        return "secondary";
      case "Failed":
        return "destructive";
      default:
        return "outline";
    }
  };

  if (isLoading) {
    return (
      <div className="p-8 flex justify-center items-center min-h-[300px]">
        <Loader className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-4 md:p-8">
      <div>
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
          Transaction History
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          View your past transactions and payments.
        </p>
      </div>

      {/* Search + Export */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <Input
          type="text"
          placeholder="Search by gateway, status, or reference..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full md:w-1/2"
        />
        <Button
          onClick={handleDownload}
          variant="outline"
          className="gap-2"
        >
          <Download size={16} />
          Export PDF
        </Button>
      </div>

      {/* Table */}
      <section className="backdrop-blur-sm bg-white/70 dark:bg-[#1a1a1a]/70 border border-gray-200/50 dark:border-gray-700/50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50 dark:bg-[#1f1f1f]">
                <TableHead className="px-6 py-4 whitespace-nowrap">
                  Date
                </TableHead>
                <TableHead className="px-6 py-4 whitespace-nowrap">
                  Gateway
                </TableHead>
                <TableHead className="px-6 py-4 whitespace-nowrap">
                  Reference
                </TableHead>
                <TableHead className="px-6 py-4 whitespace-nowrap text-right">
                  Amount
                </TableHead>
                <TableHead className="px-6 py-4 whitespace-nowrap text-center">
                  Status
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentTransactions.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-6 text-muted-foreground"
                  >
                    No matching transactions found.
                  </TableCell>
                </TableRow>
              ) : (
                currentTransactions.map((transaction, index) => (
                  <TableRow key={transaction.reference || index}>
                    <TableCell className="px-6 py-4">
                      {new Date(transaction.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      {transaction.gateway}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      {transaction.reference}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-right">
                      ₦
                      {Number(transaction.amount).toLocaleString("en-NG", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-center">
                      <Badge
                        variant={getStatusBadgeVariant(transaction.status)}
                      >
                        {transaction.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage((prev) => Math.max(1, prev - 1));
                    }}
                    aria-disabled={currentPage === 1}
                    tabIndex={currentPage === 1 ? -1 : undefined}
                    className={
                      currentPage === 1
                        ? "pointer-events-none opacity-50"
                        : undefined
                    }
                  />
                </PaginationItem>
                {[...Array(totalPages)].map((_, index) => (
                  <PaginationItem key={index}>
                    <PaginationLink
                      href="#"
                      isActive={currentPage === index + 1}
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentPage(index + 1);
                      }}
                    >
                      {index + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1));
                    }}
                    aria-disabled={currentPage === totalPages}
                    tabIndex={currentPage === totalPages ? -1 : undefined}
                    className={
                      currentPage === totalPages
                        ? "pointer-events-none opacity-50"
                        : undefined
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </section>
    </div>
  );
}
